import React, { useEffect, useState, useRef } from "react";
import { X, Plus, Trash2, Save, RotateCcw, Music2, Video, Star, Image as ImageIcon, Upload, Loader2, Link as LinkIcon, Users, CalendarDays, Wallet } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useToast } from "../hooks/use-toast";
import { getYouTubeId } from "../utils/youtube";
import { apiUploadImage, apiResolveAudio } from "../api";

const uid = () => Math.random().toString(36).slice(2, 9);

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-white/45">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full px-3 h-10 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white placeholder:text-white/30 focus:border-[#8b5cf6]/60"
      />
    </label>
  );
}

const blankArtist = () => ({
  id: uid(), name: "New Artist", verified: true, genre: "HIP-HOP", monthlyListeners: "0",
  heroImage: "https://images.pexels.com/photos/4722576/pexels-photo-4722576.jpeg", bio: "",
  songs: [{ id: uid(), title: "New Single", label: "SINGLE", price: 1.99, plays: "0", cover: "", audioUrl: "" }],
  videos: [], events: [],
  membership: { title: "EXCLUSIVE MEMBERSHIP", price: 4.99, description: "", perks: [] },
});

export default function EditPanel() {
  const { editMode, setEditMode, portal, savePortal, savePayoutSettings } = useApp();
  const { toast } = useToast();
  const [draft, setDraft] = useState(portal);
  const [idx, setIdx] = useState(0);          // which artist is being edited
  const [tab, setTab] = useState("portal");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");        // generic upload/resolve indicator key
  const [payout, setPayout] = useState({ paypalEmail: "", bankName: "", bankAccount: "", bankRouting: "", fanPrice: "" });
  const [savingPayout, setSavingPayout] = useState(false);

  const fileRef = useRef(null);
  const target = useRef({ kind: null, songId: null, videoId: null });

  useEffect(() => {
    if (editMode) {
      setDraft(JSON.parse(JSON.stringify(portal)));
      setIdx(0);
      setTab("portal");
      const p = portal.payout || {};
      setPayout({
        paypalEmail: p.paypalEmail || "", bankName: p.bankName || "",
        bankAccount: p.bankAccount || "", bankRouting: p.bankRouting || "",
        fanPrice: portal.fanPrice != null ? String(portal.fanPrice) : "",
      });
    }
  }, [editMode, portal]);

  if (!editMode) return null;

  const art = draft.artists[idx] || draft.artists[0];
  const setArtists = (artists) => setDraft((d) => ({ ...d, artists }));
  const updateArt = (patch) => setArtists(draft.artists.map((a, i) => (i === idx ? { ...a, ...patch } : a)));

  const updateSong = (sid, patch) => updateArt({ songs: art.songs.map((s) => (s.id === sid ? { ...s, ...patch } : s)) });
  const addSong = () => updateArt({ songs: [...art.songs, { id: uid(), title: "New Single", label: "SINGLE", price: 1.99, plays: "0", cover: art.songs[0]?.cover || "", audioUrl: "" }] });
  const removeSong = (sid) => updateArt({ songs: art.songs.filter((s) => s.id !== sid) });

  const updateVideo = (vid, patch) => updateArt({ videos: art.videos.map((v) => (v.id === vid ? { ...v, ...patch } : v)) });
  const addVideo = () => updateArt({ videos: [...art.videos, { id: uid(), title: "New Video", thumbnail: "", youtubeId: "" }] });
  const removeVideo = (vid) => updateArt({ videos: art.videos.filter((v) => v.id !== vid) });

  const updateEvent = (eid, patch) => updateArt({ events: (art.events || []).map((e) => (e.id === eid ? { ...e, ...patch } : e)) });
  const addEvent = () => updateArt({ events: [...(art.events || []), { id: uid(), type: "show", title: "New Event", date: "", location: "", description: "", ticketUrl: "", ticketPrice: null }] });
  const removeEvent = (eid) => updateArt({ events: (art.events || []).filter((e) => e.id !== eid) });

  // uploads
  const pick = (kind, ids = {}) => { target.current = { kind, ...ids }; if (fileRef.current) fileRef.current.click(); };
  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    const { kind, songId, videoId } = target.current;
    if (!file) return;
    setBusy(`${kind}:${songId || videoId || "hero"}`);
    try {
      const res = await apiUploadImage(file);
      if (kind === "hero") updateArt({ heroImage: res.url });
      else if (kind === "background") setDraft((d) => ({ ...d, backgroundImage: res.url }));
      else if (kind === "cover") updateSong(songId, { cover: res.url });
      else if (kind === "audio") updateSong(songId, { audioUrl: res.url });
      else if (kind === "thumb") updateVideo(videoId, { thumbnail: res.url });
      toast({ title: "Uploaded", description: "Press Save to apply." });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Try a smaller file.", variant: "destructive" });
    } finally {
      setBusy(""); target.current = { kind: null }; if (fileRef.current) fileRef.current.value = "";
    }
  };
  const resolveAudio = async (s) => {
    if (!s.audioUrl?.startsWith("http")) { toast({ title: "Paste a link first", variant: "destructive" }); return; }
    setBusy(`resolve:${s.id}`);
    try {
      const res = await apiResolveAudio(s.audioUrl);
      updateSong(s.id, { audioUrl: res.audioUrl });
      toast({ title: "Found playable audio", description: "Press Save to apply." });
    } catch (err) {
      toast({ title: "Couldn't resolve", description: err?.response?.data?.detail || "Upload the MP3 instead.", variant: "destructive" });
    } finally { setBusy(""); }
  };

  // portal management
  const setType = (t) => {
    if (t === "solo" && draft.artists.length > 1) {
      setArtists([draft.artists[idx]]); setIdx(0);
    }
    setDraft((d) => ({ ...d, portalType: t }));
  };
  const addArtist = () => {
    if (draft.portalType !== "multi") { toast({ title: "Switch to Multi-Artist first" }); return; }
    if (draft.artists.length >= (draft.maxArtists || 10)) { toast({ title: "Max 10 artists reached" }); return; }
    const next = [...draft.artists, blankArtist()];
    setArtists(next); setIdx(next.length - 1); setTab("artist");
  };
  const removeArtist = (i) => {
    if (draft.artists.length <= 1) { toast({ title: "At least one artist is required" }); return; }
    const next = draft.artists.filter((_, j) => j !== i);
    setArtists(next); setIdx(0);
  };

  const save = async () => {
    setSaving(true);
    const cleaned = {
      ...draft,
      artists: draft.artists.map((a) => ({
        ...a,
        songs: a.songs.map((s) => ({ ...s, price: parseFloat(s.price) || 0 })),
        events: (a.events || []).map((e) => ({ ...e, ticketPrice: e.ticketPrice === "" || e.ticketPrice == null ? null : parseFloat(e.ticketPrice) })),
        membership: { ...a.membership, price: parseFloat(a.membership?.price) || 4.99 },
      })),
    };
    const res = await savePortal(cleaned);
    setSaving(false);
    if (res.ok) { toast({ title: "Saved", description: "Portal updated." }); setEditMode(false); }
    else toast({ title: "Couldn't save", description: res.message, variant: "destructive" });
  };

  const savePayout = async () => {
    setSavingPayout(true);
    const payload = {
      paypalEmail: payout.paypalEmail.trim() || null,
      bankName: payout.bankName.trim() || null,
      bankAccount: payout.bankAccount.trim() || null,
      bankRouting: payout.bankRouting.trim() || null,
      fanPrice: payout.fanPrice === "" ? null : parseFloat(payout.fanPrice),
    };
    const res = await savePayoutSettings(payload);
    setSavingPayout(false);
    if (!res.ok) toast({ title: "Couldn't save", description: res.message, variant: "destructive" });
  };

  const tabs = [
    { id: "portal", label: "Portal", icon: Users },
    { id: "artist", label: "Artist", icon: ImageIcon },
    { id: "songs", label: "Songs", icon: Music2 },
    { id: "videos", label: "Videos", icon: Video },
    { id: "events", label: "Events", icon: CalendarDays },
    { id: "member", label: "Member", icon: Star },
    { id: "payout", label: "Payout", icon: Wallet },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-stretch justify-end">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditMode(false)} />
      <input ref={fileRef} type="file" accept="image/*,audio/*" className="hidden" onChange={onFile} />
      <div className="relative z-10 w-full max-w-[460px] bg-[#0d0d0f] border-l border-white/10 flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-white/10">
          <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase">Edit Portal</h3>
          <button onClick={() => setEditMode(false)} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10"><X size={16} /></button>
        </div>

        {draft.portalType === "multi" && (
          <div className="px-3 py-2 border-b border-white/10 flex gap-2 overflow-x-auto no-scrollbar">
            {draft.artists.map((a, i) => (
              <button key={a.id} onClick={() => setIdx(i)} className={`shrink-0 px-3 h-8 rounded-full text-[12px] ${i === idx ? "bg-[#8b5cf6] text-white" : "bg-white/5 text-white/60"}`}>
                {a.name || `Artist ${i + 1}`}
              </button>
            ))}
            <button onClick={addArtist} className="shrink-0 px-3 h-8 rounded-full text-[12px] bg-white/5 text-[#a98bff] flex items-center gap-1"><Plus size={13} /> Add</button>
          </div>
        )}

        <div className="flex gap-1 px-3 py-2 border-b border-white/10 overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] ${tab === t.id ? "bg-[#8b5cf6] text-white" : "text-white/55 hover:bg-white/5"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {tab === "portal" && (
            <>
              <span className="text-[11px] uppercase tracking-wide text-white/45">Portal plan</span>
              <div className="grid grid-cols-2 gap-3">
                {[{ k: "solo", t: "Solo", p: "$4.99/mo", d: "One artist" }, { k: "multi", t: "Multi-Artist", p: "$10.00/mo", d: "Up to 10 artists" }].map((o) => (
                  <button key={o.k} onClick={() => setType(o.k)} className={`text-left p-3 rounded-xl border ${draft.portalType === o.k ? "border-[#8b5cf6] bg-[#8b5cf6]/10" : "border-white/10 bg-white/[0.02]"}`}>
                    <p className="text-[14px] font-semibold text-white">{o.t}</p>
                    <p className="text-[13px] text-[#a98bff]">{o.p}</p>
                    <p className="text-[11px] text-white/45 mt-0.5">{o.d}</p>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-white/40">Membership is the fan paywall for the Exclusive section. Set your own fan price in the Payout tab.</p>

              <div className="pt-3 border-t border-white/5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wide text-white/45">Portal background image</span>
                  <button onClick={() => pick("background")} disabled={busy === "background:hero"} className="flex items-center gap-1 text-[12px] text-[#a98bff] hover:text-white disabled:opacity-60">
                    {busy === "background:hero" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
                  </button>
                </div>
                <input value={draft.backgroundImage ?? ""} onChange={(e) => setDraft((d) => ({ ...d, backgroundImage: e.target.value }))} placeholder="Paste URL or upload (optional)" className="w-full px-3 h-10 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white placeholder:text-white/30 focus:border-[#8b5cf6]/60" />
                {draft.backgroundImage && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={draft.backgroundImage} alt="" className="w-full h-24 object-cover rounded-lg border border-white/10" />
                    <button onClick={() => setDraft((d) => ({ ...d, backgroundImage: null }))} className="shrink-0 text-red-400/80 hover:text-red-400" title="Remove background"><Trash2 size={16} /></button>
                  </div>
                )}
                <p className="text-[10px] text-white/30 mt-1">Shown behind your whole portal. Press Save to apply.</p>
              </div>
              {draft.portalType === "multi" && (
                <div className="pt-2 border-t border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-wide text-white/45">Artists ({draft.artists.length}/{draft.maxArtists || 10})</span>
                    <button onClick={addArtist} className="text-[12px] text-[#a98bff] flex items-center gap-1"><Plus size={13} /> Add artist</button>
                  </div>
                  {draft.artists.map((a, i) => (
                    <div key={a.id} className="flex items-center gap-2 py-1.5">
                      <button onClick={() => { setIdx(i); setTab("artist"); }} className="flex-1 text-left text-[13px] text-white truncate">{a.name || `Artist ${i + 1}`}</button>
                      <button onClick={() => removeArtist(i)} className="text-red-400/80 hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "artist" && (
            <>
              <Field label="Artist name" value={art.name} onChange={(v) => updateArt({ name: v })} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Genre" value={art.genre} onChange={(v) => updateArt({ genre: v })} />
                <Field label="Monthly listeners" value={art.monthlyListeners} onChange={(v) => updateArt({ monthlyListeners: v })} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wide text-white/45">Hero image</span>
                  <button onClick={() => pick("hero")} disabled={busy === "hero:hero"} className="flex items-center gap-1 text-[12px] text-[#a98bff] hover:text-white disabled:opacity-60">
                    {busy === "hero:hero" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload from device
                  </button>
                </div>
                <input value={art.heroImage ?? ""} onChange={(e) => updateArt({ heroImage: e.target.value })} placeholder="Paste URL or upload" className="w-full px-3 h-10 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white placeholder:text-white/30 focus:border-[#8b5cf6]/60" />
              </div>
              {art.heroImage && <img src={art.heroImage} alt="" className="w-full h-28 object-cover rounded-lg border border-white/10" />}
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-white/45">Bio</span>
                <textarea value={art.bio} onChange={(e) => updateArt({ bio: e.target.value })} rows={3} className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white focus:border-[#8b5cf6]/60" />
              </label>
              <label className="flex items-center gap-2 text-[13px] text-white/70">
                <input type="checkbox" checked={!!art.verified} onChange={(e) => updateArt({ verified: e.target.checked })} className="accent-[#8b5cf6] w-4 h-4" /> Show "S71 Verified" badge
              </label>
            </>
          )}

          {tab === "songs" && (
            <>
              {art.songs.map((s, i) => (
                <div key={s.id} className="rounded-xl border border-white/10 p-3 space-y-3 bg-white/[0.02]">
                  <div className="flex items-center justify-between"><span className="text-[11px] text-white/40">Song {i + 1}</span><button onClick={() => removeSong(s.id)} className="text-red-400/80 hover:text-red-400"><Trash2 size={15} /></button></div>
                  <Field label="Title" value={s.title} onChange={(v) => updateSong(s.id, { title: v })} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Price ($)" type="number" value={s.price} onChange={(v) => updateSong(s.id, { price: v })} />
                    <Field label="Plays" value={s.plays} onChange={(v) => updateSong(s.id, { plays: v })} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1"><span className="text-[11px] uppercase tracking-wide text-white/45">Cover</span>
                      <button onClick={() => pick("cover", { songId: s.id })} disabled={busy === `cover:${s.id}`} className="flex items-center gap-1 text-[12px] text-[#a98bff] hover:text-white disabled:opacity-60">{busy === `cover:${s.id}` ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload</button>
                    </div>
                    <div className="flex items-center gap-2">{s.cover && <img src={s.cover} alt="" className="w-10 h-10 rounded-md object-cover shrink-0" />}<input value={s.cover ?? ""} onChange={(e) => updateSong(s.id, { cover: e.target.value })} placeholder="URL or upload" className="flex-1 px-3 h-10 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white" /></div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1"><span className="text-[11px] uppercase tracking-wide text-white/45">Audio</span>
                      <div className="flex items-center gap-3">
                        <button onClick={() => resolveAudio(s)} disabled={busy === `resolve:${s.id}`} className="flex items-center gap-1 text-[12px] text-white/55 hover:text-white disabled:opacity-60">{busy === `resolve:${s.id}` ? <Loader2 size={13} className="animate-spin" /> : <LinkIcon size={13} />} Resolve</button>
                        <button onClick={() => pick("audio", { songId: s.id })} disabled={busy === `audio:${s.id}`} className="flex items-center gap-1 text-[12px] text-[#a98bff] hover:text-white disabled:opacity-60">{busy === `audio:${s.id}` ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload MP3</button>
                      </div>
                    </div>
                    <input value={s.audioUrl ?? ""} onChange={(e) => updateSong(s.id, { audioUrl: e.target.value })} placeholder="Direct MP3 URL, or share link then Resolve" className="w-full px-3 h-10 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[13px] text-white" />
                    <p className="text-[10px] text-white/30 mt-1">Suno/Spotify share pages aren't direct files — upload the MP3.</p>
                  </div>
                </div>
              ))}
              <button onClick={addSong} className="w-full h-11 rounded-xl border border-dashed border-white/15 text-[13px] text-white/60 flex items-center justify-center gap-1.5 hover:bg-white/5"><Plus size={15} /> Add song</button>
            </>
          )}

          {tab === "videos" && (
            <>
              {art.videos.map((v, i) => (
                <div key={v.id} className="rounded-xl border border-white/10 p-3 space-y-3 bg-white/[0.02]">
                  <div className="flex items-center justify-between"><span className="text-[11px] text-white/40">Video {i + 1}</span><button onClick={() => removeVideo(v.id)} className="text-red-400/80 hover:text-red-400"><Trash2 size={15} /></button></div>
                  <Field label="Title" value={v.title} onChange={(val) => updateVideo(v.id, { title: val })} />
                  <Field label="YouTube link or ID" value={v.youtubeId} onChange={(val) => updateVideo(v.id, { youtubeId: getYouTubeId(val) })} placeholder="Paste a YouTube or Shorts link" />
                  <div>
                    <div className="flex items-center justify-between mb-1"><span className="text-[11px] uppercase tracking-wide text-white/45">Thumbnail</span>
                      <button onClick={() => pick("thumb", { videoId: v.id })} disabled={busy === `thumb:${v.id}`} className="flex items-center gap-1 text-[12px] text-[#a98bff] hover:text-white disabled:opacity-60">{busy === `thumb:${v.id}` ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload</button>
                    </div>
                    <div className="flex items-center gap-2">{v.thumbnail && <img src={v.thumbnail} alt="" className="w-16 h-10 rounded-md object-cover shrink-0" />}<input value={v.thumbnail ?? ""} onChange={(e) => updateVideo(v.id, { thumbnail: e.target.value })} placeholder="URL or upload" className="flex-1 px-3 h-10 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white" /></div>
                  </div>
                </div>
              ))}
              <button onClick={addVideo} className="w-full h-11 rounded-xl border border-dashed border-white/15 text-[13px] text-white/60 flex items-center justify-center gap-1.5 hover:bg-white/5"><Plus size={15} /> Add video</button>
            </>
          )}

          {tab === "events" && (
            <>
              <p className="text-[11px] text-white/40">Bulletin board shown in the Exclusive section: show dates, releases & news. Add a ticket price to sell in-app (QR), or a ticket link.</p>
              {(art.events || []).map((e, i) => (
                <div key={e.id} className="rounded-xl border border-white/10 p-3 space-y-3 bg-white/[0.02]">
                  <div className="flex items-center justify-between"><span className="text-[11px] text-white/40">Entry {i + 1}</span><button onClick={() => removeEvent(e.id)} className="text-red-400/80 hover:text-red-400"><Trash2 size={15} /></button></div>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wide text-white/45">Type</span>
                    <select value={e.type} onChange={(ev) => updateEvent(e.id, { type: ev.target.value })} className="mt-1 w-full px-3 h-10 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white">
                      <option value="show">Live show</option><option value="release">Release</option><option value="news">News / info</option>
                    </select>
                  </label>
                  <Field label="Title" value={e.title} onChange={(v) => updateEvent(e.id, { title: v })} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Date" type="date" value={e.date} onChange={(v) => updateEvent(e.id, { date: v })} />
                    <Field label="Location" value={e.location} onChange={(v) => updateEvent(e.id, { location: v })} />
                  </div>
                  <label className="block"><span className="text-[11px] uppercase tracking-wide text-white/45">Details</span>
                    <textarea value={e.description} onChange={(ev) => updateEvent(e.id, { description: ev.target.value })} rows={2} className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white" /></label>
                  {e.type === "show" && (
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Ticket price ($)" type="number" value={e.ticketPrice ?? ""} onChange={(v) => updateEvent(e.id, { ticketPrice: v })} placeholder="In-app (QR)" />
                      <Field label="Ticket link" value={e.ticketUrl} onChange={(v) => updateEvent(e.id, { ticketUrl: v })} placeholder="External (optional)" />
                    </div>
                  )}
                </div>
              ))}
              <button onClick={addEvent} className="w-full h-11 rounded-xl border border-dashed border-white/15 text-[13px] text-white/60 flex items-center justify-center gap-1.5 hover:bg-white/5"><Plus size={15} /> Add entry</button>
            </>
          )}

          {tab === "member" && (
            <>
              <Field label="Membership title" value={art.membership.title} onChange={(v) => updateArt({ membership: { ...art.membership, title: v } })} />
              <div className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-[12px] text-white/55">
                The fan's monthly price is what YOU set in the <span className="text-white">Payout</span> tab — collected into your own PayPal.
              </div>
              <label className="block"><span className="text-[11px] uppercase tracking-wide text-white/45">Description</span>
                <textarea value={art.membership.description} onChange={(e) => updateArt({ membership: { ...art.membership, description: e.target.value } })} rows={3} className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white" /></label>
              <div><span className="text-[11px] uppercase tracking-wide text-white/45">Perks (one per line)</span>
                <textarea value={(art.membership.perks || []).join("\n")} onChange={(e) => updateArt({ membership: { ...art.membership, perks: e.target.value.split("\n").filter(Boolean) } })} rows={4} className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white" /></div>
            </>
          )}

          {tab === "payout" && (
            <>
              <div className="px-3 py-2.5 rounded-lg bg-[#8b5cf6]/10 border border-[#8b5cf6]/30 text-[12px] text-white/70 leading-relaxed">
                Your fans' payments (membership, singles &amp; tickets) go <span className="text-white font-medium">directly to your PayPal</span>. The platform only charges your $11/mo registration.
              </div>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-white/45">Fan monthly subscription price ($)</span>
                <input type="number" min="0" step="0.01" value={payout.fanPrice} onChange={(e) => setPayout((p) => ({ ...p, fanPrice: e.target.value }))} placeholder="e.g. 4.99" className="mt-1 w-full px-3 h-10 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white placeholder:text-white/30 focus:border-[#8b5cf6]/60" data-testid="payout-fan-price" />
                <span className="text-[10px] text-white/35">Only your fans see this, after opening your exclusive link.</span>
              </label>

              <div className="pt-2 border-t border-white/5">
                <span className="text-[11px] uppercase tracking-wide text-white/45">PayPal (receive fan payments)</span>
                <input type="email" value={payout.paypalEmail} onChange={(e) => setPayout((p) => ({ ...p, paypalEmail: e.target.value }))} placeholder="your-paypal@email.com" className="mt-1 w-full px-3 h-10 rounded-lg bg-white/[0.05] border border-white/10 outline-none text-[14px] text-white placeholder:text-white/30 focus:border-[#8b5cf6]/60" data-testid="payout-paypal-email" />
                <span className="text-[10px] text-white/35">Required to accept fan payments. Fans can pay with any card — no PayPal account needed.</span>
              </div>

              <div className="pt-2 border-t border-white/5 space-y-3">
                <span className="text-[11px] uppercase tracking-wide text-white/45">Bank (optional — for your records)</span>
                <Field label="Bank name" value={payout.bankName} onChange={(v) => setPayout((p) => ({ ...p, bankName: v }))} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Account number" value={payout.bankAccount} onChange={(v) => setPayout((p) => ({ ...p, bankAccount: v }))} />
                  <Field label="Routing number" value={payout.bankRouting} onChange={(v) => setPayout((p) => ({ ...p, bankRouting: v }))} />
                </div>
              </div>

              <button onClick={savePayout} disabled={savingPayout} className="w-full h-11 rounded-xl bg-[#8b5cf6] text-white text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-[#7c4ef0] disabled:opacity-60" data-testid="payout-save-btn">
                {savingPayout ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save payout &amp; pricing
              </button>
            </>
          )}

        </div>

        <div className="px-5 pt-3 pb-20 border-t border-white/10 flex items-center gap-2">
          <button onClick={() => setEditMode(false)} className="px-3 h-11 rounded-xl bg-white/5 text-[13px] text-white/70 hover:bg-white/10">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-white text-black text-[14px] font-semibold hover:bg-white/85 disabled:opacity-60">
            <Save size={16} /> {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
